/**
 * Breadcrumb trail.
 *
 * Every page renders one: the information architecture is deliberately deep, so
 * the trail is what keeps a nested page from feeling like a dead end.
 */

import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router'

import { cn } from '../../lib/format'

export interface BreadcrumbItem {
  label: string
  /** Omit on the last item: the current page is not a link. */
  to?: string
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[]
  className?: string
}

export function Breadcrumb({ items, className }: BreadcrumbProps) {
  return (
    <nav aria-label="Breadcrumb" className={cn('text-muted-foreground text-xs', className)}>
      <ol className="flex flex-wrap items-center gap-1">
        {items.map((item, index) => {
          const isLast = index === items.length - 1
          return (
            <li key={`${item.label}-${index}`} className="flex items-center gap-1">
              {item.to && !isLast ? (
                // `min-w-6` keeps the touch target at least 24px wide where the
                // label is a single short word, which the English trail has.
                <Link
                  to={item.to}
                  className="hover:text-foreground inline-flex min-w-6 items-center justify-center py-1.5 transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <span aria-current={isLast ? 'page' : undefined} className={isLast ? 'text-foreground' : undefined}>
                  {item.label}
                </span>
              )}
              {!isLast ? <ChevronRight className="size-3" aria-hidden /> : null}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
